/**
 * Currency formatting utilities
 *
 * Standalone utilities for currency formatting when not using the context
 */

import { CurrencyConfig, DEFAULT_CURRENCY } from '@/config/currency';

export interface FormatCurrencyOptions {
  /** Currency configuration to use (takes a full CurrencyConfig object) */
  currency?: CurrencyConfig;
  /** Override decimal places */
  decimalPlaces?: number;
  /** Show symbol */
  showSymbol?: boolean;
  /** Use full currency formatting with Intl.NumberFormat */
  useIntl?: boolean;
}

/**
 * Format a number as currency
 *
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 *
 * @example
 * ```ts
 * formatCurrency(9.99) // "$9.99"
 * formatCurrency(9.99, { showSymbol: false }) // "9.99"
 * formatCurrency(9.99, { useIntl: true }) // "$9.99" (with full localization)
 * ```
 */
export function formatCurrency(
  amount: number | null,
  options: FormatCurrencyOptions = {}
): string {
  const {
    currency = DEFAULT_CURRENCY,
    decimalPlaces = currency.decimalPlaces,
    showSymbol = true,
    useIntl = false,
  } = options;

  if (amount === null) {
    return showSymbol
      ? currency.symbolPosition === 'before'
        ? `${currency.symbol}---`
        : `---${currency.symbol}`
      : '---';
  }

  // Use Intl.NumberFormat for full localization
  if (useIntl) {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(amount);
  }

  // Simple formatting with symbol
  const formatted = amount.toFixed(decimalPlaces);

  if (!showSymbol) {
    return formatted;
  }

  return currency.symbolPosition === 'before'
    ? `${currency.symbol}${formatted}`
    : `${formatted}${currency.symbol}`;
}

/**
 * Format a rate per minute
 *
 * @param rate - The rate to format
 * @param options - Formatting options
 * @returns Formatted rate string with currency symbol
 *
 * @example
 * ```ts
 * formatRate(0.05) // "$0.05/min"
 * ```
 */
export function formatRate(
  rate: number,
  options: FormatCurrencyOptions = {}
): string {
  return formatCurrency(rate, options);
}

/**
 * Format an amount in cents as currency
 *
 * @param amountInCents - The amount in cents to format (null for N/A)
 * @param options - Formatting options
 * @returns Formatted currency string
 *
 * @example
 * ```ts
 * formatCurrencyFromCents(999) // "$9.99"
 * formatCurrencyFromCents(null) // "N/A"
 * ```
 */
export function formatCurrencyFromCents(
  amountInCents: number | null,
  options: FormatCurrencyOptions = {}
): string {
  if (amountInCents === null) {
    return formatCurrency(null, options);
  }
  return formatCurrency(amountInCents / 100, options);
}

/**
 * Parse a currency string to a number
 *
 * @param value - The currency string to parse
 * @returns The numeric value
 *
 * @example
 * ```ts
 * parseCurrency("$9.99") // 9.99
 * parseCurrency("9.99") // 9.99
 * ```
 */
export function parseCurrency(value: string): number {
  // Remove all non-numeric characters except decimal point and minus
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}
